import logger from '~/services/logger';
import { getURLSegments } from './validations';
import { convertAndParseStringArray } from '~/services/layout/layout';
import type { Feed, UIComponent } from '~/services/layout/index.ts';

export const LANGUAGE_SELECTOR_DEFAULT_LANGUAGE = 'en';
export const LANGUAGE_SELECTOR_CLIENT_STORAGE_KEY = 'language';
export const LANGUAGE_SELECTOR_ENDPOINT = '/api/language-selector';

/**
 * The viewer's language choice is kept in client storage, which the server
 * cannot read. Standalone routes that are rendered on the server and carry no
 * language segment in their URL - /login above all - therefore have no way of
 * knowing what the viewer picked. This cookie mirrors that choice so those
 * routes can honour it. It is intentionally readable by both sides: the client
 * writes it when the language is selected, the server reads it off the request.
 */
export const LANGUAGE_SELECTOR_COOKIE_KEY = 'zapp_language';

const LANGUAGE_SELECTOR_COOKIE_MAX_AGE_SECONDS = 60 * 60 * 24 * 365;

/**
 * getSupportedLanguages returns languages supported by
 * the application defined in the envrionment variable `languages`.
 * In case of exception - function returns English as a default language.
 */
export function getSupportedLanguages(): string[] {
  const languagesString: string | undefined = process?.env?.languages;

  if (!languagesString) return [LANGUAGE_SELECTOR_DEFAULT_LANGUAGE];

  const parseLanguagesString: string[] =
    convertAndParseStringArray(languagesString);

  try {
    if (parseLanguagesString.length === 0)
      throw new Error('Empty production languages array');

    return parseLanguagesString;
  } catch (error: any) {
    logger.info(
      `Failed to parse languages env var, returning [${LANGUAGE_SELECTOR_DEFAULT_LANGUAGE}]\nerror: ${error}`
    );

    return [LANGUAGE_SELECTOR_DEFAULT_LANGUAGE];
  }
}

/**
 * getURLLanguage returns first segment of the URL assuming
 * it's a language code. If the retreived language code is not
 * supported - function returns 'unknown' string.
 */
export function getURLLanguage(pathname: string): string | 'unknown' {
  const segments = getURLSegments(pathname);
  const supportedLanguages = getSupportedLanguages();

  const urlLanguage: string | undefined =
    supportedLanguages.find((lang) => lang === segments[0]) || 'unknown';

  return urlLanguage;
}

/**
 * getLanguageCodeFromURL resolves the language the request should be served in.
 * It prefers the language segment of the URL, falls back to the `language`
 * query param and finally to the first supported language.
 */
export function getLanguageCodeFromURL(url: URL): string {
  return getLanguageFromURL(url) || getSupportedLanguages()[0];
}

/**
 * getLanguageFromURL returns the language carried by the URL itself - the
 * language segment first, then the `language` query param - or undefined when
 * the URL says nothing about the language.
 */
function getLanguageFromURL(url: URL): string | undefined {
  const urlLanguage = getURLLanguage(url.pathname);

  if (urlLanguage !== 'unknown') return urlLanguage;

  return url.searchParams.get('language') || undefined;
}

/**
 * parseCookieString parses a raw cookie header into a key-value object.
 */
function parseCookieString(
  cookieString: string | null | undefined
): Record<string, string> {
  if (!cookieString) return {};

  return cookieString.split(';').reduce<Record<string, string>>((acc, part) => {
    const [rawKey, ...rawValue] = part.split('=');
    const key = rawKey?.trim();
    const value = rawValue.join('=');

    if (key) {
      try {
        acc[key] = decodeURIComponent(value);
      } catch (error) {
        // A malformed cookie must not break the request it arrived on.
        acc[key] = value;
      }
    }

    return acc;
  }, {});
}

/**
 * getLanguageFromCookie returns the language the viewer selected, as mirrored
 * into the language cookie. Values that are not supported by the app are
 * ignored, so a stale cookie can never pin the app to a language it no longer
 * offers.
 */
export function getLanguageFromCookie(
  cookieString: string | null | undefined
): string | undefined {
  const language =
    parseCookieString(cookieString)[LANGUAGE_SELECTOR_COOKIE_KEY];

  if (!language) return undefined;

  return getSupportedLanguages().includes(language) ? language : undefined;
}

/**
 * storeLanguageInCookie mirrors the viewer's language choice into a cookie so
 * that server-rendered routes without a language segment in their URL can read
 * it. No-op outside the browser.
 */
export function storeLanguageInCookie(language: string | undefined): void {
  if (!language || typeof document === 'undefined') return;

  try {
    document.cookie = `${LANGUAGE_SELECTOR_COOKIE_KEY}=${encodeURIComponent(
      language
    )}; path=/; max-age=${LANGUAGE_SELECTOR_COOKIE_MAX_AGE_SECONDS}; samesite=lax`;
  } catch (error) {
    logger.info(`Failed to store the selected language in a cookie: ${error}`);
  }
}

/**
 * normalizeTag lowercases a language tag for comparison. Language tags are
 * case-insensitive (RFC 5646), so `pt-br` and `pt-BR` are the same language.
 */
function normalizeTag(tag: string): string {
  return tag.trim().toLowerCase();
}

/**
 * primarySubtag returns a tag's primary language subtag: `pt-BR` -> `pt`.
 */
function primarySubtag(tag: string): string {
  return normalizeTag(tag).split('-')[0];
}

/**
 * matchSupportedLanguage finds the configured language that best matches one
 * requested language tag, and returns it **exactly as configured**. Returning
 * the configured spelling matters: the value is sent on as
 * `x-applicaster-language-code`, so an app configured for `pt-BR` must be
 * told `pt-BR` and never a flattened `pt` it has no localizations for.
 *
 * Matching widens in three steps, so a more specific match always wins:
 *
 * 1. the whole tag          — requested `pt-BR` against configured `pt-BR`
 * 2. the requested primary  — requested `fr-CH` against configured `fr`
 * 3. a configured language sharing the requested primary — requested `pt`
 *    or `pt-PT` against configured `pt-BR`
 *
 * Step 3 is what keeps a Portuguese reader on a Portuguese-only app from
 * being handed the app's default language instead.
 */
function matchSupportedLanguage(
  requestedTag: string,
  supportedLanguages: string[]
): string | undefined {
  const requested = normalizeTag(requestedTag);
  const primary = primarySubtag(requestedTag);

  const byWholeTag = (lang: string) => normalizeTag(lang) === requested;
  const byRequestedPrimary = (lang: string) => normalizeTag(lang) === primary;
  const bySharedPrimary = (lang: string) => primarySubtag(lang) === primary;

  return (
    supportedLanguages.find(byWholeTag) ||
    supportedLanguages.find(byRequestedPrimary) ||
    supportedLanguages.find(bySharedPrimary)
  );
}

/**
 * getLanguageFromAcceptLanguage returns the viewer's most preferred language
 * that the app actually supports, read from the `Accept-Language` request
 * header, or undefined when none of them is supported. The value returned is
 * the language as configured for the app, not as the browser spelled it.
 *
 * This is the server-side counterpart of the `window.navigator.language` check
 * in getStoredLanguage: the browser sends this header on the very first
 * document request, before any client code has run and before any cookie
 * exists. It is what lets a route reached cold - /login opened straight from a
 * bookmark, a shared link or a redirect - still be served in the viewer's own
 * language rather than the app's default.
 */
export function getLanguageFromAcceptLanguage(
  acceptLanguage: string | null | undefined
): string | undefined {
  if (!acceptLanguage) return undefined;

  const supportedLanguages = getSupportedLanguages();

  const preferred = acceptLanguage
    .split(',')
    .map((part) => {
      const [tag, ...params] = part.trim().split(';');

      // Parameter names are case-insensitive (RFC 9110), so `Q=0` carries
      // the same refusal as `q=0` and must not be read as an absent q.
      const qParam = params
        .map((param) => param.trim())
        .find((param) => param.toLowerCase().startsWith('q='));

      const quality = qParam
        ? Number.parseFloat(qParam.slice(qParam.indexOf('=') + 1))
        : 1;

      return {
        tag: tag?.trim() || '',
        quality: Number.isFinite(quality) ? quality : 0,
      };
    })
    // A q value of 0 means the viewer explicitly does not want that
    // language. `*` is dropped: it names no language we could match.
    .filter(({ tag, quality }) => tag && tag !== '*' && quality > 0)
    .sort((a, b) => b.quality - a.quality);

  for (const { tag } of preferred) {
    const match = matchSupportedLanguage(tag, supportedLanguages);

    if (match) return match;
  }

  return undefined;
}

/**
 * getLanguageCodeFromRequest resolves the language a server-rendered request
 * should be served in, in this order:
 *
 * 1. what the URL says - its language segment, then the `language` query param
 * 2. the language the viewer selected, read from the language cookie
 * 3. the viewer's browser language, read from the `Accept-Language` header
 * 4. the first supported language
 *
 * Step 3 matters because the cookie in step 2 is written by client code that
 * only runs on the home route. A viewer whose first page in a given browser is
 * /login itself has no cookie yet, and without it the login screen would fall
 * back to the app's default language even though the browser told us which
 * language the viewer reads.
 *
 * Use this instead of getLanguageCodeFromURL on routes that are reached
 * without a language segment in their URL, such as /login.
 */
export function getLanguageCodeFromRequest(request: Request): string {
  try {
    const url = new URL(request.url);

    return (
      getLanguageFromURL(url) ||
      getLanguageFromCookie(request.headers.get('cookie')) ||
      getLanguageFromAcceptLanguage(request.headers.get('accept-language')) ||
      getSupportedLanguages()[0]
    );
  } catch (error) {
    logger.info(`Failed to resolve the request language: ${error}`);

    return getSupportedLanguages()[0];
  }
}

/**
 * Get the language labels for the supported languages based on the default theme v2 localizations.
 * @param supportedLanguages - array of supported languages
 * @param pluginConfigurations - array of plugin configurations to get the default theme config and its localizations
 * @returns an object with language codes as keys and their corresponding labels as values
 */
export function getLanguagesLabels({
  supportedLanguages,
  pluginConfigurations,
}: {
  supportedLanguages: string[];
  pluginConfigurations: any;
}) {
  try {
    const defaultThemeV2Config = pluginConfigurations.find(
      (config: any) => config.plugin?.identifier === 'default_theme_v2'
    );

    return supportedLanguages.reduce((acc: Record<string, string>, lang) => {
      acc[lang] =
        defaultThemeV2Config?.configuration_json?.localizations?.[lang]
          ?.language_label || lang;
      return acc;
    }, {});
  } catch (error: any) {
    logger.error(`Error generating language labels: ${error.message}`);
    return {};
  }
}

/**
 * Generate language selector feed.
 * @param languagesLabels - A record of language codes and their corresponding labels.
 * @param uiComponent - The UI component for which the feed is being generated.
 * @returns A feed object containing entries for each supported language.
 */
export function generateLanguageSelectorFeed({
  languagesLabels,
  uiComponent,
}: {
  languagesLabels: Record<string, string>;
  uiComponent: UIComponent;
}): Feed {
  const uiComponentId = uiComponent?.id;

  const baseLanguageSelectorFeed = {
    extensions: {
      role: 'language_selector',
    },
    id: `language-selector-feed-${uiComponentId}`,
    title: '',
    type: {
      value: 'feed',
    },
  };

  try {
    const supportedLanguages = Object.keys(languagesLabels);

    const feedEntries = supportedLanguages.map((lang, i) => {
      const title = languagesLabels[lang] || lang;

      return {
        _role: 'language_selector',
        extensions: {
          tag: lang,
        },
        id: `language-selector-entry-${lang}-${uiComponentId}-${i}`,
        title,
        type: {
          value: 'action',
        },
      };
    });

    return {
      entry: feedEntries,
      ...baseLanguageSelectorFeed,
    };
  } catch (error: any) {
    logger.error(`Error generating language selector feed: ${error.message}`);
    return {
      entry: [],
      ...baseLanguageSelectorFeed,
    };
  }
}
